import React from 'react';
import {
  EnvelopeSimple,
  Hourglass,
  SealCheck,
  WarningCircle,
  XCircle,
} from '@phosphor-icons/react';

import { StatusShell } from '../../design-system/components/shells';
import { AuthField, AuthInput } from '../../design-system/patterns/auth';
import {
  StatusAction,
  StatusForm,
  StatusNote,
  StatusReason,
  StatusStack,
} from '../../design-system/patterns/status';
import { routes } from '../../routes/routes';

/**
 * Email verification, the view. Pure: every input is a prop, every output a
 * callback.
 *
 * The container (`EmailVerification.jsx`) reads the token and the address out
 * of the query string, calls the API, classifies the failure and fires the
 * toasts. Nothing here does any of it.
 *
 * SIX STATES, NOT TWO
 * -------------------
 * The old page had a boolean, `verified`, and a string, `error`, and so drew
 * exactly two screens: "Email Verification" and "Email Verified!". Every way
 * this route can actually fail landed in the same red bar under the same
 * headline, which is how a user with an expired link ends up retrying the same
 * dead link. The states are genuinely different and get genuinely different
 * screens:
 *
 *   awaiting          no token in the URL. The user opened the page, not the
 *                     link. Neutral: nothing has failed. Offer the resend.
 *   verifying         a token is in flight. Neutral, because we do not know.
 *   verified          success. The one honest next command is signing in.
 *   already-verified  ALSO a success, not an error. Nothing to fix, so no
 *                     resend and no red anywhere on the page.
 *   expired           error disc. The link is dead and the ONLY useful thing
 *                     on the page is a new one, so the resend is the primary.
 *   failed            error disc, reason in words, resend still offered.
 *
 * The button is INK in all six. A green "Continue to sign in" would be using
 * colour to mean two things at once; the disc already carries the outcome.
 */

const SIGN_IN = 'Continue to sign in';

/** The resend block: the address the new link should go to, plus a nudge. */
function ResendForm({ email, onEmailChange, onResend, busy, sent, error }) {
  // Presentational state only, the same kind `AuthPasswordInput` keeps for its
  // reveal toggle. An empty field is not a mistake the user has made yet, so it
  // does not go red on arrival; the flat disabled command already says the page
  // is waiting on something. It goes red once they have been in the field and
  // left it empty, which IS a mistake.
  const [touched, setTouched] = React.useState(false);

  const handleKeyDown = (event) => {
    // The primary command lives in the shell's actions slot, which is outside
    // this block, so there is no form element for Enter to submit. Wire it by
    // hand rather than move the command out of the anatomy.
    if (event.key === 'Enter' && email && !busy) {
      event.preventDefault();
      onResend();
    }
  };

  return (
    <StatusStack>
      {error && <StatusReason>{error}</StatusReason>}
      {sent && !error && (
        <StatusNote>
          A new link is on its way. It can take a minute to arrive, and it is worth checking your
          spam folder.
        </StatusNote>
      )}
      <StatusForm>
        <AuthField
          label="Email address"
          hint="We send the link to this address."
          error={touched && !email ? 'Enter the address you signed up with' : undefined}
        >
          <AuthInput
            type="email"
            name="email"
            placeholder="you@example.co.za"
            autoComplete="email"
            value={email}
            disabled={busy}
            onChange={(event) => onEmailChange(event.target.value)}
            onBlur={() => setTouched(true)}
            onKeyDown={handleKeyDown}
          />
        </AuthField>
      </StatusForm>
    </StatusStack>
  );
}

export function EmailVerificationView({
  status = 'awaiting',
  email = '',
  onEmailChange = () => {},
  onResend = () => {},
  resending = false,
  sent = false,
  error,
}) {
  if (status === 'verifying') {
    return (
      <StatusShell
        tone="neutral"
        icon={Hourglass}
        title="Verifying your email"
        description="This only takes a moment."
      />
    );
  }

  if (status === 'verified') {
    return (
      <StatusShell
        tone="success"
        icon={SealCheck}
        title="Your email is verified"
        description="Your Sleevr account is ready. Sign in to start a watchlist, follow the sets you collect, and check out the moment you find the card."
        actions={
          <>
            <StatusAction to={routes.LOGIN}>{SIGN_IN}</StatusAction>
            <StatusAction to={routes.MARKETPLACE} variant="ghost">
              Browse the marketplace
            </StatusAction>
          </>
        }
      />
    );
  }

  if (status === 'already-verified') {
    return (
      <StatusShell
        tone="success"
        icon={SealCheck}
        title="This email is already verified"
        description={
          email
            ? `${email} was verified earlier, so there is nothing left to do here.`
            : 'This address was verified earlier, so there is nothing left to do here.'
        }
        actions={<StatusAction to={routes.LOGIN}>{SIGN_IN}</StatusAction>}
      />
    );
  }

  if (status === 'expired' || status === 'failed') {
    const expired = status === 'expired';
    return (
      <StatusShell
        tone="error"
        icon={expired ? WarningCircle : XCircle}
        title={expired ? 'That verification link has expired' : 'We could not verify that link'}
        description={
          expired
            ? 'Verification links are short lived on purpose. Send yourself a fresh one and it will work.'
            : 'The link may have been used already, or it may have been cut short by your email client.'
        }
        actions={
          <>
            <StatusAction onClick={onResend} disabled={!email || resending}>
              {resending ? 'Sending' : 'Send a new link'}
            </StatusAction>
            <StatusAction to={routes.LOGIN} variant="ghost">
              Back to sign in
            </StatusAction>
          </>
        }
      >
        <ResendForm
          email={email}
          onEmailChange={onEmailChange}
          onResend={onResend}
          busy={resending}
          sent={sent}
          error={expired ? undefined : error}
        />
      </StatusShell>
    );
  }

  return (
    <StatusShell
      tone="neutral"
      icon={EnvelopeSimple}
      title="Check your inbox"
      description={
        email
          ? `We sent a verification link to ${email}. Open it and your account is live.`
          : 'We sent you a verification link. Open it and your account is live.'
      }
      actions={
        <>
          <StatusAction onClick={onResend} disabled={!email || resending}>
            {resending ? 'Sending' : 'Resend the link'}
          </StatusAction>
          <StatusAction to={routes.LOGIN} variant="ghost">
            Back to sign in
          </StatusAction>
        </>
      }
    >
      <ResendForm
        email={email}
        onEmailChange={onEmailChange}
        onResend={onResend}
        busy={resending}
        sent={sent}
        error={error}
      />
    </StatusShell>
  );
}

export default EmailVerificationView;
